/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

import { cloneDeep } from "lodash";

import { TUserTypeValue } from "pages/Users/Shared/BulkActions/Guest/ChangeUserType/constants/user-types";
import {
  ActionTypeView,
  CheckUsersStatus,
  SelectedActionType,
  SelectedTargetType,
} from "pages/Users/Shared/BulkActions/types/types";

import { BulkActionAction, BulkActionActionType } from "../actions/BulkAction";

type InitialState = {
  // Drowpdown state
  selectedTarget: SelectedTargetType;
  actionType: SelectedActionType;
  isDropdownsVisible: boolean;

  // get users details
  checkUsersStatus: CheckUsersStatus;

  // view state
  actionTypeView: ActionTypeView;

  //send Invitation state
  sendInvitationTypes: {
    email: boolean;
    sms: boolean;
  };

  //accept Invitation state
  acceptInvitationParams: {
    pending_only: boolean;
    not_sent_only: boolean;
    rejected_only: boolean;
  };

  //reject Invitation state
  rejectInvitationParams: {
    pending_only: boolean;
    not_sent_only: boolean;
    accepted_only: boolean;
  };

  //send Registration Link state
  sendRegistrationLinkTypes: {
    email: boolean;
    sms: boolean;
  };
  sendInvitationDecision: "ignore" | "create";
  deleteGuestOption: "unlink" | "permanently";
  userTypeOption?: TUserTypeValue;
  email_type?: string;
  send_email_is_disabled?: boolean;
  apply_validation_rules?: boolean;
  blinkIdStatus: "pending_review" | "approved";
};

const initialState: InitialState = {
  // Drowpdown state
  selectedTarget: "",
  actionType: "",
  isDropdownsVisible: true,

  // get users details
  checkUsersStatus: CheckUsersStatus.no_check,

  // view state
  actionTypeView: ActionTypeView.InitialState,

  //send Invitation state
  sendInvitationTypes: {
    email: false,
    sms: false,
  },
  //accept Invitation state
  acceptInvitationParams: {
    pending_only: true,
    not_sent_only: true,
    rejected_only: false,
  },
  //reject Invitation state
  rejectInvitationParams: {
    pending_only: true,
    not_sent_only: false,
    accepted_only: false,
  },
  //send Registration Link state
  sendRegistrationLinkTypes: {
    email: false,
    sms: false,
  },
  sendInvitationDecision: "ignore",
  deleteGuestOption: "unlink",
  userTypeOption: undefined,
  apply_validation_rules: true,
  blinkIdStatus: "pending_review",
};

const BulkActionReducer = (
  state: InitialState = initialState,
  action: BulkActionAction,
): InitialState => {
  switch (action.type) {
    case BulkActionActionType.SELECTED_TARGET:
      return {
        ...state,
        selectedTarget: action.payload,
      };
    case BulkActionActionType.ACTION_TYPE_VIEW:
      return {
        ...state,
        actionTypeView: action.payload,
      };
    case BulkActionActionType.IS_DROPDOWNS_VISIBLE:
      return {
        ...state,
        isDropdownsVisible: action.payload,
      };

    case BulkActionActionType.UPDATE_VIEW:
      return {
        ...state,
        ...action.payload,
      };

    case BulkActionActionType.SEND_INVITATION_STATE:
      return {
        ...state,
        sendInvitationTypes: action.payload.sendInvitationTypes
          ? {
              ...state.sendInvitationTypes,
              ...action.payload.sendInvitationTypes,
            }
          : state.sendInvitationTypes,
        sendInvitationDecision:
          action.payload.sendInvitationDecision ?? state.sendInvitationDecision,
      };

    case BulkActionActionType.ACCEPT_INVITATION_STATE:
      return {
        ...state,
        acceptInvitationParams: {
          ...state.acceptInvitationParams,
          ...action.payload,
        },
      };

    case BulkActionActionType.REJECT_INVITATION_STATE:
      return {
        ...state,
        rejectInvitationParams: {
          ...state.rejectInvitationParams,
          ...action.payload,
        },
      };

    case BulkActionActionType.SEND_REGISTRATION_LINK_STATE:
      return {
        ...state,
        sendRegistrationLinkTypes: action.payload.sendRegistrationLinkTypes
          ? {
              ...state.sendRegistrationLinkTypes,
              ...action.payload.sendRegistrationLinkTypes,
            }
          : state.sendRegistrationLinkTypes,
      };

    case BulkActionActionType.CHANGE_DELETE_GUEST_OPTION:
      return {
        ...state,
        deleteGuestOption: action.payload.deleteGuestOption,
      };

    case BulkActionActionType.CHANGE_USER_TYPE:
      return {
        ...state,
        userTypeOption: action.payload.userTypeOption,
      };

    case BulkActionActionType.SEND_EMAIL:
      return {
        ...state,
        ...(action.payload.emailType && {
          email_type: action.payload.emailType,
        }),
        ...((action.payload.sendEmailIsDisabled ||
          action.payload.sendEmailIsDisabled === false) && {
          send_email_is_disabled: action.payload.sendEmailIsDisabled,
        }),
        ...((action.payload.applyValidationRules ||
          action.payload.applyValidationRules === false) && {
          apply_validation_rules: action.payload.applyValidationRules,
        }),
      };

    case BulkActionActionType.CREATE_APPLICATION_STATUS:
      return {
        ...state,
        blinkIdStatus: action.payload.blinkIdStatus,
      };

    case BulkActionActionType.RESET_STATE:
      return cloneDeep(initialState);
    default:
      return state;
  }
};

export default BulkActionReducer;
